import { useState } from "react";
import { CalendarClock, Loader2 } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TooltipButton } from "@/components/common/Tooltips";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const MIN_MOTIVO = 10;

const DIAS_ANCLA = [
  { value: "lunes", label: "Lunes" },
  { value: "martes", label: "Martes" },
  { value: "miercoles", label: "Miércoles" },
  { value: "jueves", label: "Jueves" },
  { value: "viernes", label: "Viernes" },
  { value: "sabado", label: "Sábado" },
];

// oct-2026 · Corrige el día de pago de las cuotas FUTURAS sin pagos (estatus Pendiente,
// pagado_acumulado 0, fecha posterior a hoy). Las cuotas pagadas, parciales o vencidas no se
// tocan y los importes nunca cambian: solo fecha_pago. Solo admin, solo créditos individuales
// desembolsados con motor de fechas "calendario".
export function CorregirFechasButton({ credito, onDone }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [anclaPago, setAnclaPago] = useState(
    credito?.condiciones_definitivas?.ancla_pago || credito?.amort_input?.ancla_pago || ""
  );
  const [busy, setBusy] = useState(false);

  if (user?.role !== "admin_entidad") return null;
  if (credito?.cancelado || ["Cancelado", "Liquidado", "Castigado"].includes(credito?.estatus)) return null;
  const desembolsado = !!credito?.fecha_disposicion
    || String(credito?.estatus_desembolso || "") === "desembolsado";
  if (!desembolsado) return null;
  if (credito?.integrantes) return null;
  if (credito?.amort_input?.fechas_engine !== "calendario") return null;
  if (["factoraje", "arrendamiento"].includes(credito?.amort_input?.motor)) return null;

  const muestraAncla = ["Semanal", "Catorcenal"].includes(credito?.periodicidad);

  const aplicar = async () => {
    const m = motivo.trim();
    if (m.length < MIN_MOTIVO) {
      toast.error(`Escribe el motivo de la corrección (mínimo ${MIN_MOTIVO} caracteres)`);
      return;
    }
    setBusy(true);
    try {
      const body = { motivo: m };
      if (muestraAncla && anclaPago) body.ancla_pago = anclaPago;
      const { data } = await api.post(`/creditos/${credito.id}/corregir-fechas`, body);
      toast.success(`Listo: ${data?.cuotas_modificadas} cuota(s) movida(s) a la nueva fecha.`);
      setOpen(false);
      setMotivo("");
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo corregir la fecha de pago");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <TooltipButton
        tooltip="Mueve las cuotas futuras sin pagos a la fecha correcta. Las cuotas pagadas no cambian."
        data-testid="corregir-fechas-btn"
        variant="outline"
        size="sm"
        className="h-8 gap-1.5 px-2.5"
        onClick={() => setOpen(true)}
      >
        <CalendarClock className="h-4 w-4" /> Corregir día de pago
      </TooltipButton>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="corregir-fechas-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Corregir día de pago</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  Solo se mueven las cuotas futuras que no tienen pagos. Las cuotas pagadas,
                  parciales o vencidas no cambian y los importes no se modifican. Las nuevas
                  fechas respetan el calendario actual de la entidad (feriados y &quot;Cobramos
                  en sábado&quot;).
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            {muestraAncla ? (
              <div>
                <Label className="mb-1 block text-xs">Nuevo día de pago</Label>
                <Select value={anclaPago} onValueChange={setAnclaPago}>
                  <SelectTrigger data-testid="corregir-fechas-ancla"><SelectValue placeholder="Elige el día" /></SelectTrigger>
                  <SelectContent>
                    {DIAS_ANCLA.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <p className="text-[13px] text-slate-500">
                Se recalcularán las fechas de las cuotas futuras con el calendario actual de la entidad.
              </p>
            )}
            <div>
              <Label className="mb-1 block text-xs">
                Motivo <span className="text-red-500">*</span>
              </Label>
              <Textarea
                data-testid="corregir-fechas-motivo"
                rows={2}
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Ej. el ancla se capturó mal en la migración y las cuotas quedaron en el día incorrecto"
              />
              <p className="mt-1 text-[11px] text-slate-400">
                Obligatorio, mínimo {MIN_MOTIVO} caracteres. Quedará en la bitácora de la entidad.
              </p>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid="corregir-fechas-volver">Volver</AlertDialogCancel>
            <AlertDialogAction
              data-testid="corregir-fechas-confirmar"
              onClick={(e) => { e.preventDefault(); aplicar(); }}
              disabled={busy || motivo.trim().length < MIN_MOTIVO}
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Aplicar corrección
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
